/**
 * Users store
 * @module store/users
 */

import { createStore, createHook } from '@thermon-platforms/react-sweet-state';
import { remove, uniq } from 'lodash';
import api, { parseErrors } from 'util/api';


// define store initial state
const initialState = {
  users: [],
  currentUser: undefined,
  loading: false,
  error: undefined,
  updated: undefined
};
// define the actions that mutate the state
export const actions = {
  // NOTE - Once we can actually save models to the API
  // there might be a better way to handle this
  forceUpdate: () => ({setState}) => {
    setState({updated: Date.now()});
  },
  query: () => async ({ getState, setState }) => {
    const now = Date.now();
    const currentState = getState();
    // If we're already loading alarms or the current data is still fresh, return
    if (currentState.loading || (now - currentState.updated) < (60 * 1000)) return;
    setState({ loading: true });
    try {
      // Fetch our data
      const res = await api('/users');
      const res_2 = await api('/users/currentUser');
      // and unwrap it
      const users = await res.json();
      const currentUser = await res_2.json();

      setState({ users, currentUser, loading: false, updated: Date.now() });
    } catch (error) {
      // If that fails, update our state with the error
      setState({ error, loading: false, updated: Date.now() });
    }
  },
  save: (user) => async ({ getState, setState }) => {
    const method = user.id ? 'PUT' : 'POST';
    const path = user.id ? `/users/${user.id}` : '/users';
    let res;

    try {
      res = await api(path, {
        method,
        body: JSON.stringify(user)
      });
    } catch (error) {
      return parseErrors(await error.json());
    }

    const data = await res.json();
    const { currentUser, users } = getState();

    if (user.id) {
      const index = users.findIndex(r => r.id == user.id);
      users.splice(index, 1, data);
    } else {
      users.push(data);
    }

    if (user.id == currentUser.id) {
      setState({ currentUser: user });
    }

    setState({ users, loading: false, updated: Date.now() });
  },
  remove: (user) => async ({ getState, setState }) => {
    const res = await api(`/users/${user.id}`, {method: 'DELETE'});
    const users = getState().users;
    remove(users, u => u.id == user.id);
    setState({ users, loading: false, updated: Date.now() });
  },
  // NOTE - the roles argument is neccesary because stores don't
  // appear to be accessible between one another. There might be
  // a better way to do this.
  checkAccess: (permission, roles=[]) => ({ getState }) => {
    const currentUser = getState().currentUser;
    if (!currentUser) return false;
    // Get all roles the current user has access to
    const currentUserRoles = roles.filter(role => currentUser.roles.includes(role.id));
    // Get all the specific permissions those roles have access to
    const allPermissions = uniq(currentUserRoles.reduce((acc, role) => {
      return acc.concat([...role.permissions]);
    }, []));
    // Check if the provided permission passes
    if (allPermissions.includes(permission)) return true;
    return false;
  }
}

// create a store type
const Store = createStore({ initialState, actions });

// create components to access store state instances
const useStore = createHook(Store);

/**
 * Expose hooks for store
 * @function useStore
 * @memberof module:store/users
 *
 * @example
 * const [usersStore, usersStoreActions] = useUsersStore();
 *
 * // Add a new user
 * usersStoreActions.add({
 *   first_name: 'Some',
 *   last_name: 'Name',
 *   email: 'some.name@thermon.com',
 *   role: 'admin'
 * });
 *
 * // Remove user
 * usersStoreActions.remove(user);
 */
export default useStore;
